import React, { type ChangeEvent, type ReactElement, useEffect, useRef, useState } from 'react'
import { Chip, CircularProgress, Typography, useTheme } from '@mui/material'
import { BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import verificationBackground from 'assets/verificationBackground.svg'
import { ReactComponent as ClearIcon } from 'assets/clearIcon.svg'
import { ReactComponent as PlusIcon } from 'assets/plusIcon.svg'
import ThemeButton from 'common/ui/theme-button'
import IconButton from 'common/ui/icon-button'
import { getFileArray } from 'utils/functions'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setErrorMessage } from 'store/app.slice'
import { sendVerificationRequest } from 'features/authentication/auth.service'
import { StoreStatus, Role } from 'common/utils/types'
import { initializeVendor, setRefDocuments } from 'features/vendor/vendor.service'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import { createFileUrl, uploadFile } from 'common/files/files.service'

import styles from './UnverifiedAccount.module.scss'

const descriptionText =
  'Vaš nalog je aktiviran i sada je potrebno da pošaljete zahtev za verifikaciju. Ukoliko želite možete da okačite referentna dokumenta koja će pomoći adminu prilikom odluke. Pod referentnim dokumentima podrazumevamo Vašu referencu sa prethodnih poslova koji ste obavljali. Zahtev za verifikaciju možete poslati i bez referntnih dokumenta.'
const UnverifiedAccount: React.FC = (): ReactElement => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dispatch = useAppDispatch()
  const { vendor } = useAppSelector((state) => state.vendor)
  const { userInfo } = useAppSelector((state) => state.auth)
  const [files, setFiles] = useState<GeneratedFile[]>([])
  const [loading, setLoading] = useState(false)

  const { sendVerificationRequestStatus } = useAppSelector((state) => state.auth)

  const { typography, palette } = useTheme()

  useEffect(() => {
    const combinedDocuments = [...(vendor?.referenceDocuments?.documents ?? []), ...files].slice(0, 3)
    void dispatch(setRefDocuments({ documents: combinedDocuments }))
  }, [files])

  const sendVerificationRequestHandler = (): void => {
    void dispatch(sendVerificationRequest()).then(() => dispatch(initializeVendor()))
  }

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setLoading(true)
    const uploadedFiles = event.target.files ? getFileArray(event.target.files) : []
    try {
      const fileData = await Promise.all(
        uploadedFiles.map(async (file) => {
          const generatedFile = await createFileUrl({
            type: GeneratedFileType.VendorRefDoc,
            mode: 'rw',
            multiple: true,
            data: { filename: file.name, vendorId: vendor?.id },
          })
          await uploadFile(generatedFile, file)
          return generatedFile
        }),
      )
      setFiles(fileData)
    } catch (e) {
      console.error('error uploading files: ', e)
      dispatch(setErrorMessage('Error uploading files'))
    } finally {
      event.target.value = ''
      setLoading(false)
    }
  }

  const handleDeleteRefDoc = (file: GeneratedFile, index: number): void => {
    const existingDocuments = [...(vendor?.referenceDocuments?.documents ?? [])]
    existingDocuments.splice(index, 1)
    void dispatch(setRefDocuments({ documents: existingDocuments }))
  }

  return (
    <div
      className={styles.infoWrapper}
      style={{
        backgroundImage: `url(${verificationBackground})`,
      }}
    >
      <div className={styles.content}>
        <div
          className={`${styles.copyWrapper} ${
            userInfo?.role === Role.consultant ? styles.copyWrapperHeight : ''
          }`}
        >
          {userInfo?.role === Role.consultant
            ? 'Vaš nalog je aktiviran i sada je potrebno da pošaljete zahtev za verifikaciju.'
            : descriptionText}
        </div>
        {userInfo?.role !== Role.consultant && (
          <div className={styles.fileWrapper}>
            <div className={styles.title}>
              <Typography variant="title3">Dodajte referentna dokumenta</Typography>
              {vendor?.referenceDocuments?.documents.length !== 0 && (
                <Typography variant="title3" sx={{ color: palette?.gray.main }}>
                  Dodato {vendor?.referenceDocuments?.documents.length}/3 dokumenta
                </Typography>
              )}
            </div>
            <div className={styles.filesPlaceholder}>
              <div className={styles.fileChipsWrapper}>
                {vendor?.referenceDocuments?.documents.map((f, i) => (
                  <Chip
                    key={i}
                    label={f.filename}
                    deleteIcon={<ClearIcon stroke={'white'} />}
                    onDelete={() => {
                      handleDeleteRefDoc(f, i)
                    }}
                    className={styles.chip}
                    sx={{
                      '& .MuiChip-label': {
                        width: 'calc(100% - 24px)',
                        ...typography.title3,
                      },
                    }}
                  />
                ))}
                {vendor?.referenceDocuments?.documents.length === 0 && (
                  <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
                    Možete da okačite maksimalno 3 dokumenta
                  </Typography>
                )}
              </div>
              {(!vendor?.referenceDocuments || vendor?.referenceDocuments?.documents.length < 3) && (
                <label htmlFor="files">
                  <IconButton
                    icon={loading ? <CircularProgress size={16} /> : <PlusIcon />}
                    onClick={() => {
                      fileInputRef?.current?.click()
                    }}
                    variant={BUTTON_VARIANTS.secondary}
                  />
                  <input
                    type="file"
                    id="files"
                    hidden
                    accept=".pdf"
                    multiple={true}
                    onChange={(e) => void handleFileSelect(e)}
                    ref={fileInputRef}
                  />
                </label>
              )}
            </div>
          </div>
        )}
        <ThemeButton
          sx={{ padding: 0, backgroundColor: palette.charts.green200.main }}
          onClick={sendVerificationRequestHandler}
          disabled={sendVerificationRequestStatus === StoreStatus.Loading}
        >
          {vendor?.referenceDocuments?.documents.length !== 0 || userInfo?.role === Role.consultant
            ? 'POŠALJI ZAHTEV'
            : 'POŠALJI ZAHTEV BEZ DOKUMENATA'}
        </ThemeButton>
      </div>
    </div>
  )
}

export default UnverifiedAccount
